import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportQueryInput, ReportsSalesByCategoryData } from "src/features/Reports/types/report.types";
import { REPORTS_SALES_BY_CATEGORY } from "src/constants/queries";
import { BtnLoader } from "src/Loader/index";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";

const headerCellClass =
  "py-3.5 align-middle text-[11px] font-semibold uppercase tracking-[0.06em] text-[#374151] border-b border-[#e5e7eb] bg-[#fafbfc] whitespace-nowrap sticky top-0 z-10";
const numHeaderClass = `${headerCellClass} text-right`;
const numCellClass = "py-3 px-3 text-right tabular-nums text-sm";
const rankCellClass = "py-3 pl-2 pr-2 text-right tabular-nums text-sm text-[#6b7280] w-9";

interface SalesByCategoryReportPageProps {
  embedded?: boolean;
  input?: ReportQueryInput | null;
}

export default function SalesByCategoryReportPage({ embedded, input: inputProp }: SalesByCategoryReportPageProps = {}) {
  const { hasRestaurant } = useAuth();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const inputBuilt = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );
  const input = embedded ? inputProp ?? null : inputBuilt;
  const { data, loading, error } = useReportQuery<ReportsSalesByCategoryData>(
    REPORTS_SALES_BY_CATEGORY,
    "reportsSalesByCategory",
    input
  );

  const rows = data?.rows ?? [];

  const content = (
    <>
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <BtnLoader />
        </div>
      ) : (
        <div className="w-full bg-white rounded-xl border border-[#e5e7eb] shadow-[0_1px_3px_rgba(0,0,0,0.06)] overflow-x-auto">
            <table className="w-full border-collapse table-auto">
              <thead>
                <tr>
                  <th className={`pl-2 pr-2 ${numHeaderClass} w-9`}>#</th>
                  <th className={`pl-2 pr-4 ${headerCellClass}`}>Category</th>
                  <th className={`px-3 ${numHeaderClass}`}>Qty Sold</th>
                  <th className={`px-3 ${numHeaderClass}`}>Gross Revenue</th>
                  <th className={`pr-4 pl-3 ${numHeaderClass}`}>% of Total Revenue</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-[#6b7280] text-sm">
                      No data for this period.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-t border-[#f8fafc] text-[#111827] transition-colors hover:bg-[#f8fafc] ${i === 0 ? "bg-[#fafbfc]" : ""}`}
                    >
                      <td className={rankCellClass}>{i + 1}</td>
                      <td className={`py-3 pl-2 pr-4 ${i === 0 ? "font-semibold text-[#0f172a]" : i < 3 ? "font-semibold text-[#111827]" : ""}`}>
                        {row.categoryName ?? "—"}
                      </td>
                      <td className={numCellClass}>{row.quantitySold ?? "—"}</td>
                      <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.grossRevenueCents ?? 0)}</td>
                      <td className={numCellClass}>{row.percentOfSales != null ? `${Number(row.percentOfSales).toFixed(1)}%` : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}
        {content}
      </>
    );
  }
  return (
    <ReportPageLayout
      hideTitle
      datePreset={datePreset}
      onDatePresetChange={(preset) => setDatePreset(preset)}
      error={error}
    >
      {content}
    </ReportPageLayout>
  );
}
